// Event weights, with the insight beside the weight it informs.
//
// The old page was a table of twenty columns, seventeen hidden, and the lift
// lived on a separate Insights page that the revamp's nav dropped. Here a row
// is the whole decision for one event, read left to right: how much more the
// leads who did it convert, how far that number can be trusted, the weight it
// suggests and the one the model holds, how often the event happens per lead,
// and the lifespan that follows from that. The statistics behind each column
// stay on the row as the "Conv. rate" and "% of conversions" figures and the
// extra columns, for anyone who wants to check.
//
// Where the statistics run out the row says "Use judgement" and offers no
// number. Nothing on it is faded: an event the training set barely saw is still
// an event Sales may care about, and the weight it gets is a business call this
// page hands over rather than pretends to make. The arithmetic behind every
// suggestion is on hover, so a person can see the lift they are looking at turn
// into the points they are offered.
//
// Reads and writes the same store as `FeaturesPage`, which stays mounted for a
// tenant without the revamp. Only `factorLoading` and `decayDays` are written,
// as before; the suggestions are derived in `eventWeights.ts` and never saved.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Card, Form } from 'react-bootstrap';

import { Feature } from '../../../models/feature_eval/FeatureEvaluation';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../../components/CheckPermissions';
import Error from '../../components/error/Error';
import Spinner from '../../components/Spinner';
import DraftNumberInput from '../../components/model/DraftNumberInput';
import { activityMark } from '../../components/model/activityType';
import {
  EventReading,
  FREQUENT_OCCURRENCES,
  LIFESPAN_MAX,
  LIFESPAN_STEPS,
  clamp,
  readEvent,
} from '../../components/model/eventWeights';
import { unitOf } from '../../components/model/segmentVocabulary';
import { rate } from '../../components/trees/performanceBands';
import EngagementTiering from '../../components/model/EngagementTiering';
import { buildAdminUrl } from '../../utils/adminUrl';
import './fineTuning.css';
import './eventWeights.css';

type SortKey = 'lift' | 'doers' | 'importance' | 'name';

interface EventWeightsPageProps {
  store?: Store;
}

interface EventWeightsPageState {
  query: string;
  activityType: string;
  sort: SortKey;
  /** 1 ascending, -1 descending. */
  direction: 1 | -1;
  /** Bumped after a weights save, so the tiering re-reads the scores. */
  revision: number;
}

interface Row {
  index: number;
  feature: Feature;
  reading: EventReading;
}

/** Log scaled, 0.1x at the left edge, 1x in the middle, 10x at the right. */
function liftBar(multiple: number | null): { left: number; width: number } {
  if (multiple === null) return { left: 50, width: 0 };
  const position = multiple <= 0 ? -1 : clamp(Math.log10(multiple), -1, 1);
  const width = Math.abs(position) * 50;
  return { left: position >= 0 ? 50 : 50 - width, width };
}

const signed = (value: number) => (value > 0 ? `+${value}` : `${value}`);
const count = (value: number) => value.toLocaleString('en-US');

export default inject('store')(
  observer(
    class EventWeightsPage extends React.Component<
      EventWeightsPageProps,
      EventWeightsPageState
    > {
      constructor(props: EventWeightsPageProps) {
        super(props);
        this.state = {
          query: '',
          activityType: 'all',
          sort: 'lift',
          direction: -1,
          revision: 0,
        };
      }

      async componentDidMount() {
        await this.props.store.loadFeatureEvaluation();
      }

      /** The same write `FeaturesPage` makes: replace the feature in the array. */
      write(index: number, patch: Partial<Feature>) {
        const { featureEvaluationPage } = this.props.store;
        const features = featureEvaluationPage.featureEvaluation.features;
        features[index] = { ...features[index], ...patch };
      }

      sortBy(key: SortKey) {
        this.setState((state) => ({
          sort: key,
          direction:
            state.sort === key
              ? ((state.direction * -1) as 1 | -1)
              : key === 'name'
              ? 1
              : -1,
        }));
      }

      rows(): Row[] {
        const { featureEvaluationPage } = this.props.store;
        const features =
          featureEvaluationPage.featureEvaluation?.features ?? [];
        const { query, activityType, sort, direction } = this.state;
        const needle = query.trim().toLowerCase();

        const rows = features
          .map((feature, index) => ({
            index,
            feature,
            reading: readEvent(feature),
          }))
          .filter(
            ({ feature }) =>
              (!needle || feature.metaEvent.toLowerCase().includes(needle)) &&
              (activityType === 'all' || feature.activityType === activityType)
          );

        const keyOf = (row: Row): number | string | null => {
          if (sort === 'name') return row.feature.metaEvent;
          if (sort === 'doers') return Number(row.feature.didX) || 0;
          if (sort === 'importance')
            return Number(row.feature.factorLoading) || 0;
          return row.reading.multiple;
        };
        return rows.sort((a, b) => {
          const x = keyOf(a);
          const y = keyOf(b);
          // Rows without a lift sink to the bottom whichever way the sort runs,
          // and keep their order among themselves: a comparator that answers 1
          // to both (a, b) and (b, a) lets the sort reshuffle them per render.
          if (x === null && y === null) return 0;
          if (x === null) return 1;
          if (y === null) return -1;
          if (typeof x === 'string' && typeof y === 'string') {
            return x.localeCompare(y) * direction;
          }
          return (Number(x) - Number(y)) * direction;
        });
      }

      /** Every event, not the filtered view: "all" has to mean all. */
      applyAll() {
        const features =
          this.props.store.featureEvaluationPage.featureEvaluation?.features ??
          [];
        features.forEach((feature, index) => {
          const reading = readEvent(feature);
          const patch: Partial<Feature> = {};
          if (reading.suggestedImportance !== null) {
            patch.factorLoading = reading.suggestedImportance;
          }
          if (reading.suggestedLifespan !== null) {
            patch.decayDays = reading.suggestedLifespan;
          }
          if (Object.keys(patch).length) this.write(index, patch);
        });
      }

      async save() {
        const { featureEvaluationPage } = this.props.store;
        const invalid = featureEvaluationPage.featureEvaluation.features.some(
          (feature) => !feature.decayDays
        );
        if (invalid) {
          featureEvaluationPage.error =
            'Feature evaluation lifespan can not be 0';
          return;
        }
        await this.props.store.saveFeatureEvaluation();
        if (!this.props.store.featureEvaluationPage.error) {
          this.setState((state) => ({ revision: state.revision + 1 }));
        }
      }

      renderHeader() {
        const { sort, direction } = this.state;
        const unit = unitOf(this.props.store.activeModel.type);
        const arrow = (key: SortKey) =>
          sort === key ? (direction < 0 ? ' ↓' : ' ↑') : '';
        const sortButton = (key: SortKey, label: string) => (
          <button
            type="button"
            className="evw-sort"
            onClick={() => this.sortBy(key)}
          >
            {label}
            {arrow(key)}
          </button>
        );
        return (
          <thead>
            <tr>
              <th>{sortButton('name', 'Event')}</th>
              <th>
                {sortButton('lift', 'Lift')}
                <div className="evw-scale">
                  <span>0.1x</span>
                  <span>1x</span>
                  <span>10x</span>
                </div>
              </th>
              <th>{sortButton('doers', 'Confidence')}</th>
              <th>
                <span title="Suggested from the lift: 10 times its natural log, so 2x is +7 and 10x is +23. Capped under limited evidence, withheld under ten.">
                  {sortButton('importance', 'Importance (points)')}
                </span>
              </th>
              <th>
                <span
                  title={`How often a converting lead did the event in the 90 days before converting. Over ${FREQUENT_OCCURRENCES} it is flagged: too frequent to weight per occurrence, weight an aggregated event instead.`}
                >
                  Times per lead
                </span>
              </th>
              <th>Lifespan (days)</th>
              <th
                title={`Of the ${unit} who did the event, the share that converted. Hover for the rate among those who did not.`}
              >
                Conv. rate
              </th>
              <th title="Of all conversions in the training set, the share that did this event.">
                % of conversions
              </th>
            </tr>
          </thead>
        );
      }

      renderRow({ index, feature, reading }: Row) {
        const { isAllowedToEdit, activeModel } = this.props.store;
        const unit = unitOf(activeModel.type);
        const mark = activityMark(feature.activityType);
        const bar = liftBar(reading.multiple);
        const importance = Number(feature.factorLoading);
        // A feature evaluated before a lifespan was stored has none; the
        // select then shows the suggested one, or the full window, never NaN.
        const lifespan = Number.isFinite(Number(feature.decayDays))
          ? Number(feature.decayDays)
          : reading.suggestedLifespan ?? LIFESPAN_MAX;
        const importanceTaken =
          reading.suggestedImportance === null ||
          importance === reading.suggestedImportance;
        const lifespanTaken =
          reading.suggestedLifespan === null ||
          lifespan === reading.suggestedLifespan;
        const pillClass =
          reading.confidence === 'reliable'
            ? 'evw-pill--reliable'
            : reading.confidence === 'limited'
            ? 'evw-pill--limited'
            : 'evw-pill--judgement';

        return (
          <tr key={feature.metaEvent}>
            <td>
              <div className="evw-event">
                <span
                  className="evw-mark"
                  title={feature.activityType}
                  style={{ background: mark.background, color: mark.color }}
                >
                  <i className={mark.icon} aria-hidden="true" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="evw-name" title={feature.metaEvent}>
                    {feature.metaEvent}
                  </div>
                  {feature.negativeUserActivity === 'yes' && (
                    <span className="evw-flag">Negative activity</span>
                  )}
                </div>
              </div>
            </td>
            <td>
              <div className="evw-lift" title={reading.confidenceTitle}>
                <div className="evw-lift-track">
                  <div className="evw-lift-axis" />
                  {reading.multiple !== null && (
                    <div
                      className={`evw-lift-fill ${
                        reading.multiple < 1 ? 'evw-lift-fill--below' : ''
                      }`}
                      style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                    />
                  )}
                </div>
                <span className="evw-lift-value">{reading.multipleLabel}</span>
              </div>
            </td>
            <td>
              <div className="evw-conf" title={reading.confidenceTitle}>
                <span className={`evw-pill ${pillClass}`}>
                  {reading.confidenceLabel}
                </span>
                <span className="evw-doers">
                  {count(Number(feature.didX) || 0)} {unit}
                </span>
              </div>
            </td>
            <td>
              <div className="evw-edit">
                <DraftNumberInput
                  data-testid="event-importance"
                  aria-label={`Importance for ${feature.metaEvent}`}
                  title={reading.importanceWhy}
                  value={feature.factorLoading}
                  disabled={!isAllowedToEdit}
                  onCommit={(value) =>
                    this.write(index, { factorLoading: value })
                  }
                />
                <button
                  type="button"
                  data-testid="event-importance-apply"
                  className={`evw-use ${
                    importanceTaken ? 'evw-use--quiet' : ''
                  }`}
                  title={reading.importanceWhy}
                  disabled={importanceTaken || !isAllowedToEdit}
                  onClick={() =>
                    this.write(index, {
                      factorLoading: reading.suggestedImportance,
                    })
                  }
                >
                  {reading.suggestedImportance === null
                    ? 'No suggestion'
                    : importanceTaken
                    ? 'Suggested'
                    : `Apply ${signed(reading.suggestedImportance)}`}
                </button>
              </div>
            </td>
            <td>
              <div className="evw-freq" title={reading.lifespanWhy}>
                <span className="evw-freq-value">
                  {reading.confidence === 'none'
                    ? '-'
                    : `${reading.occurrences.toFixed(1)}x`}
                </span>
                {/* The one reading that changes what to do with the event.
                    The rest of the column is the number, which on real data
                    sits between 1x and 4x for nearly every event. */}
                {reading.frequent && (
                  <span className="evw-pill evw-pill--limited">Frequent</span>
                )}
              </div>
            </td>
            <td>
              <div className="evw-edit">
                <select
                  data-testid="event-lifespan"
                  aria-label={`Lifespan for ${feature.metaEvent}`}
                  title={reading.lifespanWhy}
                  value={lifespan}
                  disabled={!isAllowedToEdit}
                  onChange={(event) =>
                    this.write(index, { decayDays: Number(event.target.value) })
                  }
                >
                  {/* A stored value off the ladder stays selectable as what it
                      is, so opening the list cannot change a saved weight. */}
                  {!LIFESPAN_STEPS.includes(lifespan) && (
                    <option value={lifespan}>{lifespan}</option>
                  )}
                  {LIFESPAN_STEPS.map((step) => (
                    <option key={step} value={step}>
                      {step}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`evw-use ${lifespanTaken ? 'evw-use--quiet' : ''}`}
                  title={reading.lifespanWhy}
                  disabled={lifespanTaken || !isAllowedToEdit}
                  onClick={() =>
                    this.write(index, { decayDays: reading.suggestedLifespan })
                  }
                >
                  {reading.suggestedLifespan === null
                    ? 'No suggestion'
                    : lifespanTaken
                    ? 'Suggested'
                    : `Apply ${reading.suggestedLifespan}`}
                </button>
              </div>
            </td>
            <td
              className="evw-num"
              title={
                reading.multiple === null
                  ? ''
                  : `${rate(
                      Number(feature.didNotDoXConversionRate) * 100
                    )} among ${unit} who did not do it`
              }
            >
              {reading.multiple === null
                ? '-'
                : rate(Number(feature.didXConversionRate) * 100)}
            </td>
            <td className="evw-num">
              {reading.multiple === null
                ? '-'
                : rate(Number(feature.recallConversions) * 100)}
            </td>
          </tr>
        );
      }

      render() {
        const { featureEvaluationPage, isAllowedToEdit, tenant } =
          this.props.store;
        const { featureEvaluation, loading, error, saving } =
          featureEvaluationPage;

        if (loading) return <Spinner />;

        const rows = this.rows();
        const activityTypes = Array.from(
          new Set(
            (featureEvaluation?.features ?? [])
              .map((feature) => feature.activityType)
              .filter(Boolean)
          )
        ).sort();
        // Counted over every event, like the button it labels.
        const pending = (featureEvaluation?.features ?? []).filter(
          (feature) => {
            const reading = readEvent(feature);
            return (
              (reading.suggestedImportance !== null &&
                Number(feature.factorLoading) !==
                  reading.suggestedImportance) ||
              (reading.suggestedLifespan !== null &&
                Number(feature.decayDays) !== reading.suggestedLifespan)
            );
          }
        ).length;

        return (
          <div className="evw mt-3 mb-5">
            {error && <Error message={error} />}
            {/* The thresholds, above the weights that move the score they
                cut. An engagement model has no trees to weigh, so this is the
                whole of what fit's Fine tuning page does for it. */}
            <EngagementTiering revision={this.state.revision} />
            <Card className="ftune-card">
              <Card.Body>
                <div className="evw-head">
                  <h5
                    className="ftune-title"
                    title="Each occurrence adds the importance to the score, fading to nothing over the lifespan. Suggestions come from the training set."
                  >
                    Event weights
                  </h5>
                  {/* Where the events themselves are defined. A link rather
                      than a control, and beside the title rather than among
                      the controls, because it leaves the page. */}
                  <a
                    className="evw-link"
                    href={buildAdminUrl(tenant, '/mapping/event_mapping')}
                  >
                    Event mapping{' '}
                    <i
                      className="fas fa-external-link-alt"
                      aria-hidden="true"
                    />
                  </a>
                </div>

                <div className="evw-tools">
                  <Form.Control
                    type="search"
                    size="sm"
                    placeholder="Search events"
                    aria-label="Search events"
                    value={this.state.query}
                    onChange={(event) =>
                      this.setState({ query: event.target.value })
                    }
                  />
                  <Form.Select
                    size="sm"
                    aria-label="Activity type"
                    value={this.state.activityType}
                    onChange={(event) =>
                      this.setState({ activityType: event.target.value })
                    }
                  >
                    <option value="all">All activity types</option>
                    {activityTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  <span className="evw-spacer" />
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,
                    <>
                      <Button
                        size="sm"
                        variant="light"
                        className="ftune-btn-quiet"
                        disabled={!isAllowedToEdit || pending === 0}
                        onClick={() => this.applyAll()}
                      >
                        Apply all suggestions{pending ? ` (${pending})` : ''}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        data-testid="event-weights-save"
                        disabled={saving || !isAllowedToEdit}
                        onClick={() => this.save()}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </div>

                <div className="evw-scroll">
                  <table className="evw-table">
                    {this.renderHeader()}
                    <tbody>{rows.map((row) => this.renderRow(row))}</tbody>
                  </table>
                  {rows.length === 0 && (
                    <p className="evw-empty">No event matches.</p>
                  )}
                </div>
                {/* Rare and destructive: it recomputes every weight from the
                    statistics and drops every hand-set one. Under the table,
                    as a link, so it is not reached for in passing. */}
                {checkPermissions(
                  PROFILES_PERMISSIONS.ARCHITECT,
                  <div className="evw-foot">
                    <button
                      type="button"
                      className="evw-link evw-link--button"
                      disabled={!isAllowedToEdit}
                      title="Recomputes every weight and lifespan from the statistics, dropping every value set by hand."
                      onClick={() => this.props.store.resetFeatureEvaluation()}
                    >
                      Reset all weights to the statistics
                    </button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        );
      }
    }
  )
);
